import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, LogOut, ArrowRight, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, track, formatApiError, loadRazorpayScript, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import { MODULES } from "@/lib/content";

const MILESTONE_LABELS = {
  org_created: "Create Organization",
  employee_added: "Add Employees",
  attendance_configured: "Configure Attendance",
  payroll_configured: "Configure Payroll",
  expense_added: "Add First Expense",
  invoice_created: "Create First Invoice",
  dashboard_viewed: "View Dashboard",
};

export const PAID_STATUSES = ["active", "past_due", "payment_failed", "cancelled"];

export function useRazorpaySubscription(onSuccess) {
  const [paying, setPaying] = useState(false);

  const startCheckout = useCallback(async (planId, billingCycle, endpoint = "/billing/subscribe") => {
    setPaying(true);
    try {
      const { data: order } = await api.post(endpoint, { plan_id: planId, billing_cycle: billingCycle });
      track("checkout_started", { plan: planId, cycle: billingCycle });
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load payment gateway. Check your connection.");
      const rzp = new window.Razorpay({
        key: order.key_id,
        subscription_id: order.subscription_id,
        name: "Mashara Finance",
        description: order.label,
        theme: { color: "#047857" },
        modal: { ondismiss: () => toast.info("Checkout closed. No payment was made.") },
        handler: async (resp) => {
          try {
            const { data: result } = await api.post("/billing/verify-subscription", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (result.status === "active") {
              track("payment_success", { plan: planId });
              track("subscription_activated", { plan: planId });
              onSuccess(order.label);
            } else {
              toast.info("Payment received. Your subscription will activate on confirmation — this can take a minute.");
              onSuccess(null);
            }
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        track("payment_failed", { plan: planId, reason: resp.error?.description });
        toast.error(`Payment failed: ${resp.error?.description || "Please try another method"}`);
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 409) {
        toast.info("You already have an active subscription. Manage it from the Subscription page.");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setPaying(false);
    }
  }, [onSuccess]);

  return { startCheckout, paying };
}

export default function AppDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [successLabel, setSuccessLabel] = useState(undefined); // undefined = none, null = pending, string = activated
  const [verifying, setVerifying] = useState(false);

  useSEO({ title: "Your Dashboard — Mashara Finance", description: "Mashara Finance account dashboard.", path: "/app", noindex: true });

  const load = useCallback(() => {
    api.get("/app/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === null) navigate("/login");
    else if (user) { load(); track("onboarding_started"); api.post("/app/milestones/dashboard_viewed").catch(() => {}); }
  }, [user, navigate, load]);

  const { startCheckout, paying } = useRazorpaySubscription((label) => {
    setSuccessLabel(label);
    load();
  });

  const completeMilestone = async (key) => {
    await api.post(`/app/milestones/${key}`).catch(() => {});
    load();
  };

  const payTrialVerification = async () => {
    setVerifying(true);
    try {
      const { data: order } = await api.post("/billing/order", { kind: "trial_verification" });
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: "Mashara Finance", description: order.label, order_id: order.order_id,
        prefill: { name: user.name, email: user.email, contact: user.mobile || "" },
        theme: { color: "#047857" },
        modal: { ondismiss: () => toast.info("Checkout closed. No payment was made.") },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            toast.success("Trial activated. Welcome aboard!");
            load();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setVerifying(false);
    }
  };

  if (!data) return <div className="py-32 text-center text-slate-500" data-testid="dashboard-loading">Loading your dashboard…</div>;

  if (successLabel !== undefined) {
    return (
      <div className="py-24 bg-slate-50 min-h-screen" data-testid="payment-success-screen">
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-700 text-white flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {successLabel ? "Subscription Activated" : "Payment Received"}
            </h1>
            <p className="text-slate-600 mt-3">
              {successLabel
                ? `Your ${successLabel} is now active. All plan features are unlocked immediately.`
                : "We are confirming your payment with Razorpay. Your subscription will activate shortly."}
            </p>
            <Button data-testid="success-dashboard-button" onClick={() => { setSuccessLabel(undefined); load(); }}
              className="mt-8 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8">
              Go to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const sub = data.subscription || {};
  const isPaid = PAID_STATUSES.includes(sub.status);
  const statusLabel = { pending_verification: "Verification Pending", trialing: "Trial Active", active: "Active",
    past_due: "Past Due", payment_failed: "Payment Failed", cancelled: "Cancelled", expired: "Expired",
    payment_pending: "Payment Pending" }[sub.status] || sub.status;
  const limits = data.limits || {};
  const usage = data.usage || { users: 0, centers: 0 };

  return (
    <div data-testid="app-dashboard" className="py-10 sm:py-16 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900" data-testid="dashboard-greeting">
              Welcome, {data.user?.name?.split(" ")[0]}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{data.organization?.name} ·{" "}
              <span className={`font-medium ${sub.status === "active" ? "text-emerald-700" : "text-amber-700"}`}>{statusLabel}</span>
              {sub.status === "trialing" && data.trial_days_left !== null && <span> · Trial ends in {data.trial_days_left} days</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="dashboard-subscription-link" onClick={() => navigate("/app/subscription")}
              className="rounded-xl border-slate-300 text-slate-700">
              <CreditCard className="w-4 h-4 mr-2" /> Subscription
            </Button>
            <Button variant="outline" data-testid="dashboard-logout-button" onClick={async () => { await logout(); navigate("/"); }}
              className="rounded-xl border-slate-300 text-slate-700">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        {sub.status === "pending_verification" && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="verification-banner">
            <div>
              <p className="font-semibold text-slate-900">Activate your {data.trial_days}-day free trial</p>
              <p className="text-sm text-slate-600 mt-1">Complete a one-time {formatINR(data.trial_verification_amount / 100)} verification payment via Razorpay (UPI, card or net-banking). No subscription is charged during the trial.</p>
            </div>
            {data.payments_configured ? (
              <Button data-testid="verify-trial-button" disabled={verifying} onClick={payTrialVerification}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white shrink-0">
                {verifying ? "Opening…" : `Pay ${formatINR(data.trial_verification_amount / 100)} & Activate`}
              </Button>
            ) : (
              <p className="text-sm text-amber-800 font-medium shrink-0" data-testid="payments-pending-note">Online payments are being configured.</p>
            )}
          </div>
        )}

        {sub.status === "payment_failed" && (
          <div className="rounded-2xl bg-red-50 border border-red-200 p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="payment-failed-banner">
            <div>
              <p className="font-semibold text-red-900">Payment issue with your subscription</p>
              <p className="text-sm text-red-700 mt-1">Your last payment failed. Your data is safe. Update payment to keep access.</p>
            </div>
            <Button data-testid="retry-payment-button" onClick={() => navigate("/app/subscription")}
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white shrink-0">Retry / Update Payment</Button>
          </div>
        )}

        {sub.status === "expired" && (
          <div className="rounded-2xl bg-slate-900 text-white p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="expired-banner">
            <div>
              <p className="font-semibold">Your {sub.plan_id === "trial" ? "trial" : "subscription"} has ended</p>
              <p className="text-sm text-slate-300 mt-1">Your data is safe. Choose a plan to continue using paid features.</p>
            </div>
            <Button data-testid="expired-subscribe-button" onClick={() => document.getElementById("upgrade-panel")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">Subscribe Now</Button>
          </div>
        )}

        {isPaid && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-8" data-testid="usage-panel">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-600 font-medium">Users</span>
                  <span className="font-semibold text-slate-900" data-testid="usage-users">{usage.users} / {limits.users ?? "Unlimited"}</span>
                </div>
                <Progress value={limits.users ? Math.min(100, (usage.users / limits.users) * 100) : 0} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-600 font-medium">Centers</span>
                  <span className="font-semibold text-slate-900" data-testid="usage-centers">{usage.centers} / {limits.centers ?? "Unlimited"}</span>
                </div>
                <Progress value={limits.centers ? Math.min(100, (usage.centers / limits.centers) * 100) : 0} className="h-2" />
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="onboarding-checklist">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-bold text-xl text-slate-900">Complete Your Setup</h2>
                <span className="text-sm font-semibold text-emerald-700" data-testid="checklist-completion">{data.completion}%</span>
              </div>
              <Progress value={data.completion} className="h-2 mb-6" />
              <div className="space-y-2">
                {Object.entries(MILESTONE_LABELS).map(([key, label]) => {
                  const doneM = data.milestones[key];
                  return (
                    <div key={key} data-testid={`milestone-${key}`}
                      className={`flex items-center justify-between rounded-xl border p-4 ${doneM ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
                      <span className={`flex items-center gap-2.5 text-sm font-medium ${doneM ? "text-emerald-800" : "text-slate-700"}`}>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center ${doneM ? "bg-emerald-600 text-white" : "bg-slate-200"}`}>
                          {doneM && <Check className="w-3 h-3" />}
                        </span>
                        {label}
                      </span>
                      {!doneM && key !== "org_created" && (
                        <Button size="sm" variant="outline" data-testid={`milestone-complete-${key}`} onClick={() => completeMilestone(key)}
                          className="rounded-lg text-xs border-slate-300">Mark done</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="modules-panel">
              <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Your Modules</h2>
              <p className="text-sm text-slate-500 mb-6">
                {isPaid ? "Based on your current plan." : sub.status === "expired" ? "Subscribe to re-activate your modules." : "Full platform access during your trial."}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.all_modules.map((m) => {
                  const allowed = data.entitlements.includes(m);
                  return (
                    <div key={m} data-testid={`module-${m}`}
                      className={`rounded-xl border p-4 ${allowed ? "border-slate-200" : "border-slate-200 bg-slate-50 opacity-75"}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm text-slate-900">{MODULES[m].name}</p>
                        {allowed ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">AVAILABLE</span>
                          : <Lock className="w-4 h-4 text-slate-400" />}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{MODULES[m].benefit}</p>
                      {!allowed && (
                        <p className="text-xs text-amber-700 mt-2 font-medium" data-testid={`module-locked-${m}`}>
                          Not Authorized for this Service. Please upgrade your subscription.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 text-white rounded-2xl p-6" id="upgrade-panel" data-testid="upgrade-panel">
              <h2 className="font-display font-bold text-lg">{isPaid ? "Change Plan" : "Choose Your Plan"}</h2>
              <p className="text-sm text-slate-400 mt-1 mb-4">
                {sub.status === "trialing" ? "Subscribe anytime — your remaining trial days are preserved." : "Secure payment via Razorpay. Cancel anytime."}
              </p>
              <div className="inline-flex bg-slate-800 rounded-full p-1 mb-5" data-testid="dashboard-billing-toggle">
                <button data-testid="dashboard-monthly" onClick={() => setBillingCycle("monthly")}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold ${billingCycle === "monthly" ? "bg-emerald-600 text-white" : "text-slate-400"}`}>Monthly</button>
                <button data-testid="dashboard-annual" onClick={() => setBillingCycle("annual")}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold ${billingCycle === "annual" ? "bg-emerald-600 text-white" : "text-slate-400"}`}>Annual</button>
              </div>
              <div className="space-y-3">
                {(data.plans || []).map((p) => {
                  const isCurrent = isPaid && data.current_plan?.id === p.id;
                  return (
                    <div key={p.id} data-testid={`upgrade-plan-${p.id}`} className={`rounded-xl border p-4 ${isCurrent ? "border-emerald-500" : "border-slate-700"}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-semibold flex items-center gap-2">{p.name}
                          {isCurrent && <span data-testid={`current-plan-badge-${p.id}`} className="text-[10px] font-bold bg-emerald-600 text-white rounded-full px-2 py-0.5">CURRENT PLAN</span>}
                        </p>
                        <p className="font-display font-bold">{formatINR(billingCycle === "annual" ? p.annual_price : p.monthly_price)}<span className="text-xs text-slate-400 font-normal">/{billingCycle === "annual" ? "yr" : "mo"}</span></p>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{p.users} · {p.centers}</p>
                      {isCurrent ? (
                        <Button size="sm" variant="outline" data-testid={`manage-plan-${p.id}`} onClick={() => navigate("/app/subscription")}
                          className="mt-3 w-full rounded-lg border-slate-600 text-slate-200 bg-transparent hover:bg-white/10 text-xs">Manage Subscription</Button>
                      ) : (
                        <Button size="sm" data-testid={`buy-plan-${p.id}`} disabled={paying || !data.payments_configured}
                          onClick={() => isPaid ? navigate("/app/subscription") : startCheckout(p.id, billingCycle)}
                          className="mt-3 w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                          {!data.payments_configured ? "Payments coming soon" : isPaid ? "Upgrade Plan" : sub.status === "trialing" || sub.status === "expired" || sub.status === "payment_pending" ? "Subscribe Now" : "Subscribe Now"}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-4 text-center">Secured by Razorpay · We never store card details</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 text-sm">Need help choosing?</h3>
              <p className="text-xs text-slate-500 mt-1 mb-3">Our team will recommend the right plan for your organization size.</p>
              <Button variant="outline" size="sm" data-testid="dashboard-demo-button" onClick={() => navigate("/demo")}
                className="rounded-lg border-slate-300 text-slate-700 text-xs w-full">Book a Demo</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
